'use client'

import { KeyboardEvent } from 'react'

import { Cell, Spacer, Text } from '@vinted/web-ui'

import { navigateToPage } from '@marketplace-web/browser/browser-navigation-util'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { KeyboardKey, onA11yKeyDown } from '@marketplace-web/vendor-abstractions/web-ui-util'
import {
  orderDetailsProtectionPoliciesClickEvent,
  TransactionSide,
} from '@marketplace-web/seller-protection/seller-protection-data'

import { BUYER_REFUND_POLICY_HELP_URL } from '../../../constants/routes'

type Props = {
  transactionId: number | undefined
  transactionStatus: number | null | undefined
  shipmentStatus: number | null | undefined
}

const ConversationDetailsBuyerProtection = ({
  transactionId,
  transactionStatus,
  shipmentStatus,
}: Props) => {
  const translate = useTranslate('buyer_protection_policies')
  const { track } = useTracking()

  const trackBuyerProtectionClick = () => {
    if (!transactionId) return

    track(
      orderDetailsProtectionPoliciesClickEvent({
        screen: 'conversation_details',
        target: 'buyer_protection_learn_more',
        transactionId: transactionId.toString(),
        userSide: TransactionSide.Buyer,
        details: JSON.stringify({
          transaction_status: transactionStatus,
          shipment_status: shipmentStatus,
        }),
      }),
    )
  }

  const openBuyerProtectionFAQLink = () => {
    trackBuyerProtectionClick()
    navigateToPage(BUYER_REFUND_POLICY_HELP_URL)
  }

  const onKeyDown = (event: KeyboardEvent) => {
    onA11yKeyDown(
      event,
      { keys: [KeyboardKey.Enter, KeyboardKey.Space] },
      () => openBuyerProtectionFAQLink,
    )
  }

  return (
    <Cell
      title={<Text text={translate('title')} type="section-heading" as="span" />}
      body={
        <>
          <Spacer size="large" />
          <Text text={translate('description')} as="span" />
          <Spacer />
          <button
            type="button"
            tabIndex={0}
            onKeyDown={onKeyDown}
            onClick={openBuyerProtectionFAQLink}
          >
            <Text text={translate('learn_more')} as="span" clickable />
          </button>
        </>
      }
      divider
    />
  )
}

export default ConversationDetailsBuyerProtection
